import type { MessagePart } from "../Processing/types";
import type { AiStreamFrame } from "../DataPipeline";

const textPart = (content: string, title = ""): MessagePart => ({
  type: "text",
  title,
  content,
  data: null,
});

/** A stated number from a summary section: "Days above average: 2,214". */
interface SummaryFact {
  label?: string;
  display?: string;
  value?: unknown;
}

function factsToPart(facts: SummaryFact[] | undefined, title: string): MessagePart[] {
  const lines = (facts ?? [])
    .map((f) => {
      const value = f.display ?? (f.value != null ? String(f.value) : "");
      return f.label && value ? `${f.label}: ${value}` : value;
    })
    .filter(Boolean)
    .map((line) => `- ${line}`);
  return lines.length ? [textPart(lines.join("\n"), title)] : [];
}

/**
 * Frame → parts to append, in order. Pure so the wire mapping is readable in
 * one place; the frames that only move the tile's status (step.*, text, error,
 * turn_done) are handled by the caller and return nothing here.
 *
 * The V2 summary UI is not in the FE: a summary route renders as flat parts
 * (prose, then the stated facts, then the blocks), so `summary.plan` and the
 * prose deltas are ignored and `summary.section.complete` is authoritative.
 *
 * `partsAppended` is how many parts this turn has already produced — the only
 * thing that tells a summary envelope carrying prose instead of sections
 * (status "empty"/"no_data"/"error") from one whose sections already rendered.
 */
export function framesToParts(frame: AiStreamFrame, partsAppended: number): MessagePart[] {
  switch (frame.type) {
    case "render":
      return frame.part ? [frame.part as MessagePart] : [];

    case "no_data":
      // A valid answer, not an error.
      return [textPart(String(frame.message ?? ""))];

    case "summary.section.complete": {
      const payload = (frame.payload ?? {}) as {
        title?: string;
        narrative?: string | null;
        error_message?: string | null;
        status?: string;
        facts?: SummaryFact[];
        blocks?: MessagePart[];
      };
      const title = payload.title ?? "";
      const prose = payload.status === "error"
        ? payload.error_message ?? "This section could not be written."
        : payload.narrative ?? "";
      return [
        ...(prose ? [textPart(prose, title)] : []),
        ...factsToPart(payload.facts, title),
        // blocks[].type is the authority on what to draw, not block_type.
        ...(payload.blocks ?? []),
      ];
    }

    case "summary.complete": {
      if (partsAppended > 0) return [];
      const message = (frame.payload as { message?: string | null } | undefined)?.message;
      return message ? [textPart(String(message))] : [];
    }

    default:
      return [];
  }
}
